<template>
  <Modal :is-open="isOpen" @close="$emit('cancel')">
    <template #header>
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 shadow-sm"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <div>
          <h2 class="text-xl font-bold text-gray-900">
            {{ initial ? 'Edit Usage Record' : 'Record Equipment Usage' }}
          </h2>
          <p class="text-xs text-gray-500 mt-0.5">
            {{
              initial
                ? 'Update operating hours, fuel consumed, and task details'
                : 'Log operating hours, fuel consumed, and field activities'
            }}
          </p>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-5">
      <!-- Loading indicator line -->
      <div v-if="loading" class="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
        <div class="bg-emerald-600 h-full animate-pulse"></div>
      </div>

      <!-- Server Error Alert -->
      <div
        v-if="serverError"
        class="p-3.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2.5"
      >
        <svg
          class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="flex-1">{{ serverError }}</div>
      </div>

      <!-- Equipment Selection -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">
          Equipment <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <select
            v-model.number="form.equipment_id"
            class="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
            :class="{ 'border-red-500 ring-1 ring-red-500': errors.equipment_id }"
          >
            <option :value="0" disabled>Select equipment...</option>
            <option v-for="eq in equipmentList" :key="eq.id" :value="eq.id">
              {{ eq.name }} ({{ eq.type || 'Equipment' }}) — {{ eq.requires_fuel ? 'Requires Fuel' : 'No Fuel' }}
            </option>
          </select>
        </div>
        <p v-if="errors.equipment_id" class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {{ errors.equipment_id }}
        </p>

        <!-- Selected Equipment Info Banner -->
        <div
          v-if="selectedEquipment"
          class="mt-2.5 bg-gray-50 border border-gray-200 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2.5 text-xs text-gray-600"
        >
          <div class="flex items-center gap-2">
            <span class="font-semibold text-gray-800">{{ selectedEquipment.name }}</span>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                selectedEquipment.status === 'active'
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-gray-200 text-gray-700'
              "
            >
              {{ selectedEquipment.status || 'active' }}
            </span>
            <span v-if="selectedEquipment.model" class="text-gray-500">
              Model: {{ selectedEquipment.model }}
            </span>
          </div>

          <div>
            <span
              v-if="selectedEquipment.requires_fuel"
              class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-medium"
            >
              ⛽ Fuel Required: {{ selectedEquipment.fuel_type || 'Fuel' }}
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium"
            >
              🌿 No Fuel Needed
            </span>
          </div>
        </div>
      </div>

      <!-- Core Metrics (Date, Hours, Fuel) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Log Date -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">
            Log Date <span class="text-red-500">*</span>
          </label>
          <input
            type="date"
            v-model="form.log_date"
            :max="todayStr"
            class="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
            :class="{ 'border-red-500 ring-1 ring-red-500': errors.log_date }"
          />
          <p v-if="errors.log_date" class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ errors.log_date }}
          </p>
        </div>

        <!-- Hours Operated -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">
            Hours Operated
          </label>
          <div class="relative">
            <input
              type="number"
              v-model.number="form.hours_used"
              step="0.1"
              min="0"
              placeholder="e.g. 4.5"
              class="w-full pl-3.5 pr-12 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
              :class="{ 'border-red-500 ring-1 ring-red-500': errors.hours_used }"
            />
            <span class="absolute right-3.5 top-2.5 text-xs text-gray-400 font-medium">hrs</span>
          </div>
          <p v-if="errors.hours_used" class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ errors.hours_used }}
          </p>
        </div>
      </div>

      <!-- Fuel Used (Highlighted when required, or optional) -->
      <div v-if="showFuel || form.fuel_used !== undefined">
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">
          Fuel Used
          <span v-if="showFuel" class="text-red-500">*</span>
          <span v-if="showFuel" class="text-xs font-normal text-amber-700 ml-1.5">
            (Required for {{ selectedEquipment?.fuel_type || 'this fuel-based equipment' }})
          </span>
        </label>
        <div class="relative">
          <input
            type="number"
            v-model.number="form.fuel_used"
            step="0.01"
            min="0"
            placeholder="e.g. 12.50"
            class="w-full pl-3.5 pr-14 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
            :class="{ 'border-red-500 ring-1 ring-red-500': errors.fuel_used }"
          />
          <span class="absolute right-3.5 top-2.5 text-xs text-gray-400 font-medium">liters</span>
        </div>
        <p v-if="errors.fuel_used" class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {{ errors.fuel_used }}
        </p>
      </div>

      <!-- Real-time Fuel Efficiency Calculation Card -->
      <div
        v-if="calculatedEfficiency"
        class="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-200 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs shadow-sm"
      >
        <div class="flex items-center gap-2.5">
          <div
            class="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-sm"
          >
            ⚡
          </div>
          <div>
            <span class="text-emerald-900 font-semibold block">Calculated Performance</span>
            <span class="text-emerald-700">Consumption Rate: <strong>{{ calculatedRate }} L/hr</strong></span>
          </div>
        </div>
        <div class="text-right">
          <span class="text-gray-500 block">Efficiency</span>
          <span class="text-sm font-bold text-emerald-800">{{ calculatedEfficiency }} hrs/L</span>
        </div>
      </div>

      <!-- Tasks Performed -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">
          Tasks Performed
        </label>
        <textarea
          v-model="form.tasks_performed"
          rows="3"
          placeholder="e.g. Plowing Field Block 4, harrowing north terrace, hauling harvest bags..."
          class="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
        ></textarea>
      </div>

      <!-- Notes -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">
          Notes & Remarks
        </label>
        <textarea
          v-model="form.notes"
          rows="2"
          placeholder="e.g. Regular engine check completed, operator John Doe, oil level normal..."
          class="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-gray-800 transition"
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          @click="$emit('cancel')"
          :disabled="loading"
          class="px-5 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 transition"
        >
          Cancel
        </button>

        <button
          type="submit"
          :disabled="loading"
          class="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <svg
            v-if="loading"
            class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {{ loading ? 'Saving...' : submitLabel }}
        </button>
      </div>
    </form>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { UsageLogCreate, UsageLog } from '@/types/usage'
import { useEquipmentStore } from '@/stores/equipmentStore'
import Modal from '@/components/shared/Modal.vue'

interface Props {
  isOpen?: boolean
  initial?: UsageLog | null
  loading?: boolean
  submitLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: true,
  initial: null,
  loading: false,
  submitLabel: '',
})

const emit = defineEmits<{
  (e: 'save', data: UsageLogCreate): void
  (e: 'cancel'): void
}>()

const equipmentStore = useEquipmentStore()

const todayStr = new Date().toISOString().slice(0, 10)

const equipmentList = computed(() =>
  Array.isArray(equipmentStore.equipment) ? equipmentStore.equipment : [],
)

const form = ref<UsageLogCreate>({
  equipment_id: 0,
  log_date: todayStr,
  hours_used: undefined,
  fuel_used: undefined,
  tasks_performed: '',
  notes: '',
})

const errors = ref<Record<string, string>>({})
const serverError = ref<string>('')

const submitLabel = computed(() => {
  if (props.submitLabel) return props.submitLabel
  return props.initial ? 'Update Record' : 'Save Record'
})

onMounted(async () => {
  if (equipmentList.value.length === 0) {
    try {
      await equipmentStore.fetchEquipment()
    } catch {
      // non-fatal
    }
  }
  if (props.initial) {
    populateFromInitial(props.initial)
  }
})

watch(
  () => props.initial,
  (v) => {
    if (v) {
      populateFromInitial(v)
    } else {
      resetForm()
    }
  },
  { immediate: true },
)

function populateFromInitial(init: UsageLog) {
  form.value.equipment_id = init.equipment_id
  form.value.log_date = init.log_date ? init.log_date.slice(0, 10) : todayStr
  form.value.hours_used =
    init.hours_used !== null && init.hours_used !== undefined
      ? Number(init.hours_used)
      : undefined
  form.value.fuel_used =
    init.fuel_used !== null && init.fuel_used !== undefined
      ? Number(init.fuel_used)
      : undefined
  form.value.tasks_performed = init.tasks_performed || ''
  form.value.notes = init.notes || ''
}

function resetForm() {
  form.value = {
    equipment_id: 0,
    log_date: todayStr,
    hours_used: undefined,
    fuel_used: undefined,
    tasks_performed: '',
    notes: '',
  }
  errors.value = {}
  serverError.value = ''
}

const selectedEquipment = computed(() =>
  equipmentList.value.find((e) => Number(e.id) === Number(form.value.equipment_id)),
)

const showFuel = computed(() => !!selectedEquipment.value?.requires_fuel)

const calculatedEfficiency = computed(() => {
  if (form.value.hours_used && form.value.fuel_used && Number(form.value.fuel_used) > 0) {
    return (Number(form.value.hours_used) / Number(form.value.fuel_used)).toFixed(2)
  }
  return null
})

const calculatedRate = computed(() => {
  if (form.value.hours_used && form.value.fuel_used && Number(form.value.hours_used) > 0) {
    return (Number(form.value.fuel_used) / Number(form.value.hours_used)).toFixed(2)
  }
  return null
})

function validate(): boolean {
  errors.value = {}
  serverError.value = ''
  let ok = true

  if (!form.value.equipment_id || form.value.equipment_id === 0) {
    errors.value.equipment_id = 'Please select an equipment'
    ok = false
  }

  if (!form.value.log_date) {
    errors.value.log_date = 'Log date is required'
    ok = false
  } else if (form.value.log_date > todayStr) {
    errors.value.log_date = 'Log date cannot be in the future'
    ok = false
  }

  if (form.value.hours_used !== undefined && form.value.hours_used !== null) {
    if (Number(form.value.hours_used) <= 0) {
      errors.value.hours_used = 'Hours used must be greater than 0'
      ok = false
    }
  }

  if (showFuel.value) {
    if (
      form.value.fuel_used === undefined ||
      form.value.fuel_used === null ||
      Number(form.value.fuel_used) <= 0
    ) {
      errors.value.fuel_used = 'Fuel used is required for this equipment (must be > 0)'
      ok = false
    }
  } else if (form.value.fuel_used !== undefined && form.value.fuel_used !== null) {
    if (Number(form.value.fuel_used) <= 0) {
      errors.value.fuel_used = 'Fuel used must be greater than 0'
      ok = false
    }
  }

  return ok
}

function handleSubmit() {
  if (!validate()) return

  const payload: UsageLogCreate = {
    equipment_id: form.value.equipment_id,
    log_date: form.value.log_date,
    hours_used:
      form.value.hours_used !== undefined && form.value.hours_used !== null
        ? Number(form.value.hours_used)
        : undefined,
    fuel_used:
      form.value.fuel_used !== undefined && form.value.fuel_used !== null
        ? Number(form.value.fuel_used)
        : undefined,
    tasks_performed: form.value.tasks_performed?.trim() || undefined,
    notes: form.value.notes?.trim() || undefined,
  }

  emit('save', payload)
}
</script>
